import { useState, useEffect, useCallback } from "react";
import { QuotationsTab } from "@/components/admin/QuotationsTab";
import { PaymentsTab } from "@/components/admin/PaymentsTab";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Eye,
  Mail,
  MessageSquare,
  BarChart3,
  Users,
  FileText,
  MousePointer,
  Loader2,
  Receipt,
  IndianRupee,
  ShieldCheck,
} from "lucide-react";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { SecurityTab } from "@/components/admin/SecurityTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-yellow-100 text-yellow-700",
  qualified: "bg-green-100 text-green-700",
  closed: "bg-stone-100 text-stone-700",
};

const AdminPage = () => {
  const [admin, setAdmin] = useState(undefined);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("inquiries");
  const [quoteInquiry, setQuoteInquiry] = useState(null);

  const getAuthHeader = useCallback(() => ({}), []);

  const loadMe = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setAdmin(data);
    } catch {
      setAdmin(null);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  useEffect(() => {
    if (admin) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inqRes, contactsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/inquiries`, { headers: getAuthHeader() }),
        axios.get(`${API}/contacts`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/summary`, { headers: getAuthHeader() }),
      ]);
      setInquiries(inqRes.data);
      setContacts(contactsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        setAdmin(null);
        toast.error("Session expired. Please sign in again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(
        `${API}/inquiries/${inquiryId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      setInquiries((prev) =>
        prev.map((inq) =>
          inq.id === inquiryId ? { ...inq, status: newStatus } : inq
        )
      );
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setAdmin(null);
  };

  if (admin === undefined) {
    return (
      <div className="min-h-screen pt-40 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
      </div>
    );
  }

  if (!admin) {
    return (
      <AdminLogin
        onLoggedIn={(_, usedRecovery) => {
          if (usedRecovery) toast.warning("You used a recovery code. Create new codes in the Security tab if you're running low.");
          loadMe();
        }}
      />
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand-200/30 pt-32 md:pt-40 lg:pt-48 pb-20">
      <div className="container-custom">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl text-stone-900">Admin Dashboard</h1>
            <p className="text-stone-600 text-sm mt-1">
              Signed in as <span data-testid="admin-signed-in-as">{admin.name} ({admin.role})</span>
            </p>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="outline"
            onClick={handleLogout}
            className="rounded-full"
          >
            Logout
          </Button>
        </div>

        {admin.role === "legacy" && (
          <div className="mb-6 rounded-xl bg-amber-50 text-amber-900 p-4 text-sm" data-testid="legacy-login-banner">
            You're using the old shared login, which has no two-step verification. Set up your personal account with the one-time setup link, then disable this login in the Security tab.
          </div>
        )}

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Eye className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-stone-900">
                      {analytics.page_views}
                    </p>
                    <p className="text-xs text-stone-500">Page Views</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <MousePointer className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-stone-900">
                      {analytics.cta_clicks}
                    </p>
                    <p className="text-xs text-stone-500">CTA Clicks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-stone-900">
                      {analytics.total_inquiries}
                    </p>
                    <p className="text-xs text-stone-500">Inquiries</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-stone-900">
                      {analytics.total_contacts}
                    </p>
                    <p className="text-xs text-stone-500">Contacts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="inquiries" className="gap-2">
              <FileText className="h-4 w-4" />
              Inquiries ({inquiries.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="quotations" className="gap-2" data-testid="admin-tab-quotations">
              <Receipt className="h-4 w-4" />
              Quotations
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2" data-testid="admin-tab-payments">
              <IndianRupee className="h-4 w-4" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2" data-testid="admin-tab-security">
              <ShieldCheck className="h-4 w-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              </div>
            ) : inquiries.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No inquiries yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inquiry) => (
                  <Card key={inquiry.id} data-testid={`inquiry-${inquiry.id}`}>
                    <CardContent className="py-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-lg text-stone-900">
                              {inquiry.company_name}
                            </h3>
                            <Badge className={statusColors[inquiry.status]}>
                              {inquiry.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-stone-600 mb-2">
                            {inquiry.contact_person} • {inquiry.email}
                          </p>
                          <p className="text-sm text-stone-500 mb-2">
                            {inquiry.country} • {inquiry.inquiry_type}
                            {inquiry.estimated_volume && ` • ${inquiry.estimated_volume}`}
                          </p>
                          {inquiry.product_interest?.length > 0 && (
                            <div className="flex gap-2 mb-2">
                              {inquiry.product_interest.map((prod) => (
                                <Badge key={prod} variant="outline" className="text-xs">
                                  {prod}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-stone-600 mt-3 bg-stone-50 p-3 rounded-lg">
                            {inquiry.message}
                          </p>
                          <p className="text-xs text-stone-400 mt-2">
                            {new Date(inquiry.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`inquiry-create-quotation-btn-${inquiry.id}`}
                            onClick={() => {
                              setQuoteInquiry(inquiry);
                              setTab("quotations");
                            }}
                          >
                            <Receipt className="h-4 w-4 mr-1" />
                            Quote
                          </Button>
                          <Select
                            value={inquiry.status}
                            onValueChange={(value) =>
                              updateInquiryStatus(inquiry.id, value)
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="qualified">Qualified</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              </div>
            ) : contacts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No contact messages yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <Card key={contact.id} data-testid={`contact-${contact.id}`}>
                    <CardContent className="py-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Mail className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-stone-900">{contact.name}</h3>
                          <p className="text-sm text-stone-600">
                            {contact.email}
                            {contact.company && ` • ${contact.company}`}
                          </p>
                          <p className="text-sm font-medium text-stone-700 mt-2">
                            {contact.subject}
                          </p>
                          <p className="text-sm text-stone-600 mt-2 bg-stone-50 p-3 rounded-lg">
                            {contact.message}
                          </p>
                          <p className="text-xs text-stone-400 mt-2">
                            {new Date(contact.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotations">
            {tab === "quotations" && (
              <QuotationsTab
                authHeader={getAuthHeader}
                prefillInquiry={quoteInquiry}
                onPrefillConsumed={() => setQuoteInquiry(null)}
              />
            )}
          </TabsContent>

          <TabsContent value="payments">
            {tab === "payments" && <PaymentsTab authHeader={getAuthHeader} />}
          </TabsContent>

          <TabsContent value="security">
            {tab === "security" && <SecurityTab admin={admin} onRefresh={loadMe} />}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Top Pages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Pages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.top_pages?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_pages.map((page, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between"
                          >
                            <span className="text-sm text-stone-600">
                              {page.page || "/"}
                            </span>
                            <span className="text-sm font-medium text-stone-900">
                              {page.views} views
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-stone-500">No data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Inquiries by Type */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Inquiries by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.inquiries_by_type?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.inquiries_by_type.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between"
                          >
                            <span className="text-sm text-stone-600 capitalize">
                              {item.type}
                            </span>
                            <span className="text-sm font-medium text-stone-900">
                              {item.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-stone-500">No data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
